import * as React from "react"
import GROUPED_TASKS_SERVICE, {
  IGroupDeleteInput,
  IGroupInput,
  IGroupMoveTaskToGroupInput,
  IGroupSubTaskInput,
  IGroupUpdateInput,
  ITasksRequestPayload,
} from "../api-fns"
import { useMutation, UseMutationResult, useQuery } from "@tanstack/react-query"
import { useParams, useSearchParams } from "react-router-dom"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { Row } from "@tanstack/react-table"
import {
  ALL_ORG_USERS_ENDPOINT,
  PRIORITY_DICTIONARY_ENDPOINT,
  SERVER_URL,
  STATUS_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useAuth } from "@pma/stores/auth-store"
import axios from "axios"
import { format } from "date-fns"
import { he } from "date-fns/locale"

interface GroupedTasksContextProps {
  groupedTasks: any[]
  groupedTasksLoading: boolean
  groupedTasksRefetch: () => void
  createGroupMutation: UseMutationResult<any, Error, IGroupInput, unknown> | null
  updateGroupMutation: UseMutationResult<any, Error, IGroupUpdateInput, unknown> | null
  deleteGroupMutation: UseMutationResult<any, Error, IGroupDeleteInput, unknown> | null
  restoreTaskGroupMutation: UseMutationResult<any, Error, any, unknown> | null
  moveTaskToGroupMutation: UseMutationResult<any, Error, IGroupMoveTaskToGroupInput, unknown> | null
  fetchSubTaskMutation: UseMutationResult<any, Error, IGroupSubTaskInput, unknown> | null
  setPagination: React.Dispatch<{ pageIndex: number; pageSize: number }>
  paginationData: {
    curPage: number
    itemsReceived: number
    itemsTotal: number
    nextPage: number
    offset: number
    pageTotal: number
    prevPage: number
    pageSize: number
  }
  setPageSorting: React.Dispatch<{
    orderBy: "asc" | "desc" | string
    sortBy: string
  }>
  pageSorting: {
    orderBy: "asc" | "desc" | string
    sortBy: string
  }
  setTaskExpandForLoading: (parentId: number, row: Row<any>) => void
  setSelectedTasks: React.Dispatch<
    Array<{
      taskId: number
      priorityId: number
      responsible_id: number
      project_id: number
      group_id: number
    }>
  >
  selectedTasks: Array<{
    taskId: number
    priorityId: number
    responsible_id: number
    project_id: number
    group_id: number
  }>
  showColumns: Array<{ id: string; title: string; show: boolean }>
  setShowColumns: React.Dispatch<Array<{ id: string; title: string; show: boolean }>>
  setSearch: React.Dispatch<string>
  search: string
  updateGroupTasks: (groupId: number, reorderedTasks: any[]) => void
  setGroupedTasks: React.Dispatch<
    Array<{
      created_at: string | number
      id: number
      name: string
      project_id: number
      user_id: number
      _tasks: any[]
    }>
  >
  lastInsertTaskId: number
  setLastInsertTaskId: React.Dispatch<number>
  orgUsers: UsersResponse
  getUserName: (activeId: string) => any
  taskStatus: Status[]
  getTaskStatusName: (statusList: string[]) => any
  taskPrioritiesList: Priorities[]
}

interface Status {
  id: number
  created_at: number
  name: string
  param_name: string
  user_id: number
  icon: string
  color: string
  canceled: boolean
  finished: boolean
  for_task: boolean
  for_project: boolean
  is_checked: boolean
}

interface Priorities {
  color: string
  created_at: number
  icon: string
  id: number
  level: number
  name: string
  organization_id: number
}

interface Role {
  id: number
  created_at: number
  name: string
}

interface Status {
  id: number
  created_at: number
  user_id: number
  is_active: boolean
  name: string
}

interface Invitation {
  created_at: number
  expired_at: number
  is_accepted: boolean
}

interface UserItem {
  id: number
  created_at: number
  name: string
  email: string
  phone_number: string
  manager_id: number
  user_picture: string | null
  role: Role
  status: Status
  _invitation: Invitation
}

interface UsersResponse {
  itemsReceived: number
  curPage: number
  nextPage: number | null
  prevPage: number | null
  offset: number
  itemsTotal: number
  pageTotal: number
  items: UserItem[]
}

interface IGroupedTasksProviderProps {
  children: React.ReactNode
}

const tableColumns = [
  { id: "id", title: "Ticket", show: true },
  { id: "created_by.name", title: "Created By", show: true },
  { id: "title", title: "Title", show: true },
  { id: "notes", title: "Notes", show: true },
  { id: "description", title: "Description", show: true },
  { id: "status.name", title: "Status", show: true },
  { id: "_priority.level", title: "Priority", show: true },
  { id: "responsible", title: "Assigned", show: true },
  { id: "_gis", title: "Gis", show: true },
  { id: "files", title: "Files", show: true },
  { id: "Duration", title: "Duration", show: true },
  { id: "sessions", title: "Time", show: true },
]

export const GroupedTasksContext = React.createContext<GroupedTasksContextProps>({
  groupedTasks: [],
  groupedTasksLoading: false,
  groupedTasksRefetch: () => {},
  createGroupMutation: null,
  updateGroupMutation: null,
  deleteGroupMutation: null,
  restoreTaskGroupMutation: null,
  moveTaskToGroupMutation: null,
  fetchSubTaskMutation: null,
  setPagination: (state) => state,
  paginationData: {
    curPage: 1,
    itemsReceived: 10,
    itemsTotal: 10,
    nextPage: 3,
    offset: 2,
    pageTotal: 4,
    prevPage: 1,
    pageSize: 50,
  },
  setPageSorting: (state) => state,
  pageSorting: {
    orderBy: "desc",
    sortBy: "",
  },
  setTaskExpandForLoading: () => {},
  setSelectedTasks: (state) => state,
  selectedTasks: [],
  showColumns: [],
  setShowColumns: (state) => state,
  setSearch: (state) => state,
  search: "",
  updateGroupTasks: () => {},
  setGroupedTasks: (state) => state,
  lastInsertTaskId: 0,
  setLastInsertTaskId: (state) => state,
  orgUsers: {
    itemsReceived: 0,
    curPage: 0,
    nextPage: null,
    prevPage: null,
    offset: 0,
    itemsTotal: 0,
    pageTotal: 0,
    items: [],
  },
  getUserName: () => {},
  taskStatus: [],
  getTaskStatusName: () => {},
  taskPrioritiesList: [],
})

const GroupedTasksProvider: React.FC<IGroupedTasksProviderProps> = ({
  children,
}: IGroupedTasksProviderProps) => {
  const QUERY_KEY = "grouped-tasks"
  const [groupedTasks, setGroupedTasks] = React.useState<any[]>([])
  const { projectId } = useParams()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const [{ pageIndex, pageSize }, setPagination] = React.useState<any>({
    pageIndex: 1,
    pageSize: 50,
  })

  const [pageSorting, setPageSorting] = React.useState({
    orderBy: "",
    sortBy: "",
  })

  const storeColumn = localStorage.getItem("tableColumn")
  const columnList = storeColumn ? JSON.parse(storeColumn) : []

  const isHebrew = locale === "he"

  const { authConfig, signIn } = useAuth()
  const [search, setSearch] = React.useState("")
  const [lastInsertTaskId, setLastInsertTaskId] = React.useState(0)
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id

  const [showColumns, setShowColumns] = React.useState<
    Array<{ id: string; title: string; show: boolean }>
  >(columnList.length ? columnList : tableColumns)

  const [selectedTasks, setSelectedTasks] = React.useState<
    Array<{
      taskId: number
      priorityId: number
      responsible_id: number
      project_id: number
      group_id: number
    }>
  >([])
  const [expandingRow, setExpandingRow] = React.useState<Row<any> | null>(null)

  const activeStatus = searchParam.get("status")
  const activeUser = searchParam.get("user")
  const activeTracker = searchParam.get("tracker")
  const fromDate = searchParam.get("from")
  const toDate = searchParam.get("to")
  const priority = searchParam.get("priority")
  const showDeletedTasks = searchParam.get("showDeletedTasks")

  /** This is for fetching data */
  const fetchTasks = async () => {
    const payload: ITasksRequestPayload = {
      project_id: projectId ? parseInt(projectId) : 0,
      paging_start_page: pageIndex,
      paging_per_page: pageSize,
    }

    if (activeStatus) {
      const statusList = activeStatus.split(",")
      payload["status_id"] = [parseInt(statusList[0])]
    }

    if (activeUser) {
      payload["responsible_id"] = [parseInt(activeUser)]
    }

    if (pageSorting.sortBy) {
      payload["sorting"] = pageSorting
    }

    if (search) {
      payload["search_text"] = search
    }

    if (activeTracker) {
      payload["by_active_sessions"] = activeTracker == "Running" ? true : false
    }

    if (showDeletedTasks) {
      payload["show_deleted"] = showDeletedTasks === "true" ? true : false
    }

    if (fromDate && toDate) {
      // Ensure fromDate and toDate are always Date objects
      const parseToDate = (value: string | null): Date => {
        if (!value) return new Date()
        // If it's a number string, treat as timestamp
        if (!isNaN(Number(value))) {
          return new Date(Number(value))
        }
        // Otherwise, try to parse as ISO/date string
        const parsed = new Date(value)
        if (isNaN(parsed.getTime())) {
          throw new Error(`Invalid date: ${value}`)
        }
        return parsed
      }
      const stateDate: Date = parseToDate(fromDate)
      const endDate: Date = parseToDate(toDate)

      payload["start_at_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }

      payload["finish_to_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }

      payload["created_at_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }
    }

    if (priority) {
      payload["priority_id"] = [parseInt(priority)]
    }

    return GROUPED_TASKS_SERVICE.getTasks(payload)
  }

  /** This is for fetching data */
  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey: [
      QUERY_KEY,
      activeUser,
      activeStatus,
      projectId,
      pageSize,
      pageIndex,
      pageSorting,
      search,
      activeTracker,
      fromDate,
      toDate,
      priority,
      showDeletedTasks,
    ],
    queryFn: fetchTasks,
  })

  const paginationData = React.useMemo(() => {
    const ungrouped_tasks = data?.data?.ungrouped_tasks
    return {
      curPage: ungrouped_tasks?.curPage,
      itemsReceived: ungrouped_tasks?.itemsReceived,
      itemsTotal: ungrouped_tasks?.itemsTotal,
      nextPage: ungrouped_tasks?.nextPage,
      offset: ungrouped_tasks?.offset,
      pageTotal: ungrouped_tasks?.pageTotal,
      prevPage: ungrouped_tasks?.prevPage,
      pageSize: pageSize,
    }
  }, [isFetching, data?.data, pageSize])

  /** This is for formatting fetched data */
  const formatTasks = (data: any): any[] => {
    if (!data || !projectId) {
      return []
    }

    const { project_groups, ungrouped_tasks } = data

    const storedData = localStorage.getItem("projects")
    const storedProjects = storedData ? JSON.parse(storedData) : { projects: {} }
    const storedTasks = storedProjects.projects?.[`project_${projectId}`] || { project_groups: [] }
    let storedGroups = storedTasks.project_groups || []

    // Map API task IDs and subtasks by group and task
    const apiTaskIdsByGroup = new Map<number, Set<number>>()
    const apiSubtasksByTask = new Map<number, Set<number>>()
    project_groups?.forEach((group: any) => {
      apiTaskIdsByGroup.set(group.id, new Set(group._tasks.map((t: any) => t.id)))
      group._tasks.forEach((task: any) => {
        if (task._subtasks && task._subtasks.length > 0) {
          apiSubtasksByTask.set(task.id, new Set(task._subtasks.map((st: any) => st.id)))
        }
      })
    })
    if (ungrouped_tasks?.items) {
      apiTaskIdsByGroup.set(-1, new Set(ungrouped_tasks.items.map((t: any) => t.id)))
      ungrouped_tasks.items.forEach((task: any) => {
        if (task._subtasks && task._subtasks.length > 0) {
          apiSubtasksByTask.set(task.id, new Set(task._subtasks.map((st: any) => st.id)))
        }
      })
    }

    // Filter stored groups and tasks, preserving valid subtasks
    storedGroups = storedGroups
      .map((sg: any) => {
        const apiTaskIds = apiTaskIdsByGroup.get(sg.id) || new Set()
        const filteredTasks = sg._tasks?.filter((t: any) => apiTaskIds.has(t.id)) || []
        return {
          ...sg,
          _tasks: filteredTasks.map((task: any) => {
            const apiSubtaskIds = apiSubtasksByTask.get(task.id) || new Set()
            const filteredSubtasks =
              task._subtasks?.filter((st: any) => apiSubtaskIds.has(st.id)) || []
            return { ...task, _subtasks: filteredSubtasks }
          }),
        }
      })
      .filter((sg: any) => {
        const apiTaskIds = apiTaskIdsByGroup.get(sg.id)
        return apiTaskIds && apiTaskIds.size > 0
      })

    const _data: any[] = []
    if (project_groups && project_groups.length > 0) {
      const nextGrouppedTasks = project_groups.map((group: any) => {
        const { _tasks } = group
        const storedGroup = storedGroups.find((sg: any) => sg.id === group.id)

        let nextTasks = _tasks.map((task: any) => {
          const storedTask = storedGroup?._tasks?.find((t: any) => t.id === task.id)
          // Merge subtasks: prioritize API, preserve local storage order for matching IDs
          const apiSubtasks = task._subtasks || []
          const storedSubtasks = storedTask?._subtasks || []
          const apiSubtaskIds = new Set(apiSubtasks.map((st: any) => st.id))
          const mergedSubtasks = [
            // Keep stored subtasks in their order if they exist in API
            ...storedSubtasks.filter((st: any) => apiSubtaskIds.has(st.id)),
            // Add new API subtasks not in local storage
            ...apiSubtasks.filter(
              (st: any) => !storedSubtasks.some((sst: any) => sst.id === st.id),
            ),
          ]

          return {
            ...task,
            tasks_group_id: group.id,
            isLoadingSubTasks: false,
            _subtasks: mergedSubtasks,
          }
        })

        // if (storedGroup && storedGroup._tasks && storedGroup._tasks.length > 0) {
        //   const storedTaskIds = storedGroup._tasks.map((t: any) => t.id)
        //   const taskMap = new Map(nextTasks.map((task: any) => [task.id, task]))
        //   const reorderedTasks = storedTaskIds
        //     .map((taskId: number) => taskMap.get(taskId))
        //     .filter((task: any) => task !== undefined)
        //   const newTasks = nextTasks.filter((task: any) => !storedTaskIds.includes(task.id))
        //   nextTasks = [...reorderedTasks, ...newTasks]
        // }
        return { ...group, _tasks: nextTasks, total_counter: nextTasks.length || 0 }
      })
      _data.push(...nextGrouppedTasks)
    }

    if (ungrouped_tasks && ungrouped_tasks?.items && ungrouped_tasks?.items?.length > 0) {
      let nextTasks = ungrouped_tasks?.items.map((task: any) => {
        const storedGroup = storedGroups.find((sg: any) => sg.id === -1)
        const storedTask = storedGroup?._tasks?.find((t: any) => t.id === task.id)
        // Merge subtasks: prioritize API, preserve local storage order for matching IDs
        const apiSubtasks = task._subtasks || []
        const storedSubtasks = storedTask?._subtasks || []
        const apiSubtaskIds = new Set(apiSubtasks.map((st: any) => st.id))
        const mergedSubtasks = [
          ...storedSubtasks.filter((st: any) => apiSubtaskIds.has(st.id)),
          ...apiSubtasks.filter((st: any) => !storedSubtasks.some((sst: any) => sst.id === st.id)),
        ]

        return {
          ...task,
          tasks_group_id: null,
          isLoadingSubTasks: false,
          _subtasks: mergedSubtasks,
        }
      })

      // const storedUngrouped = storedGroups.find((sg: any) => sg.id === -1)
      // if (storedUngrouped && storedUngrouped._tasks && storedUngrouped._tasks.length > 0) {
      //   const storedTaskIds = storedUngrouped._tasks.map((t: any) => t.id)
      //   const taskMap = new Map(nextTasks.map((task: any) => [task.id, task]))
      //   const reorderedTasks = storedTaskIds
      //     .map((taskId: number) => taskMap.get(taskId))
      //     .filter((task: any) => task !== undefined)
      //   const newTasks = nextTasks.filter((task: any) => !storedTaskIds.includes(task.id))
      //   nextTasks = [...reorderedTasks, ...newTasks]
      // }

      _data.push({
        id: -1,
        name: "Ungrouped",
        project_id: ungrouped_tasks?.items[0]?.project_id ?? -1,
        user_id: ungrouped_tasks?.items[0]?.creator_id ?? -1,
        _tasks: [...nextTasks],
        total_counter: ungrouped_tasks?.itemsTotal || 0,
        created_at: null,
      })
    } else {
      _data.push({
        id: -1,
        name: "Ungrouped",
        project_id: -1,
        user_id: -1,
        _tasks: [],
        total_counter: 0,
        created_at: null,
      })
    }

    const updatedLocalData = {
      ...storedProjects,
      projects: {
        ...storedProjects.projects,
        [`project_${projectId}`]: { project_groups: _data },
      },
    }
    localStorage.setItem("projects", JSON.stringify(updatedLocalData))

    return _data
  }
  /** This is for settings tasks data and initializing local storage */
  React.useEffect(() => {
    if (!isFetching && data && projectId) {
      const formattedTasks = formatTasks(data.data)
      setGroupedTasks(formattedTasks)
    }
  }, [isFetching, data, projectId])

  const createGroup = (data: IGroupInput): Promise<any> => {
    return GROUPED_TASKS_SERVICE.createGroup(data)
  }

  const createGroupMutation = useMutation({
    mutationFn: (data: IGroupInput) => createGroup(data),
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: `${data.data.name} ${translate("task group added successfully")}`,
        variant: "success",
      })
    },
    onError: (error) => {
      const message = error.message
      toast({
        title: translate("Error"),
        description: message || translate("Error occurred while creating task-group"),
        variant: "error",
      })
    },
  })

  const updateGroup = (data: IGroupUpdateInput): Promise<any> => {
    return GROUPED_TASKS_SERVICE.updateGroup(data)
  }

  const updateGroupMutation = useMutation({
    mutationFn: (data: IGroupUpdateInput) => updateGroup(data),
    onSuccess: () => {
      // refetch()
      // toast({
      //   title: "success",
      //   description: (
      //     <pre className='mt-2 w-[340px] rounded-md p-4'>
      //       <code className='text-wrap text-white'>{translate("task-group deleted successfully")}</code>
      //     </pre>
      //   ),
      //   variant: "success",
      // })
    },
    onError: (error) => {
      const message = error.message
      toast({
        title: translate("Error"),
        description: message || translate("Error occurred while updating task-group"),
        variant: "error",
      })
    },
  })

  const deleteGroup = (data: IGroupDeleteInput): Promise<any> => {
    return GROUPED_TASKS_SERVICE.deleteGroup(data)
  }

  const deleteGroupMutation = useMutation({
    mutationFn: (data: IGroupDeleteInput) => deleteGroup(data),
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("task-group deleted successfully"),
        variant: "success",
      })
    },
    onError: (error) => {
      const message = error.message
      toast({
        title: translate("Error"),
        description: message || translate("Error occurred while deleting task-group"),
        variant: "error",
      })
    },
  })

  const moveTaskToGroup = (data: IGroupMoveTaskToGroupInput): Promise<any> => {
    return GROUPED_TASKS_SERVICE.moveTaskToGroup(data)
  }

  const moveTaskToGroupMutation = useMutation({
    mutationFn: (data: IGroupMoveTaskToGroupInput) => moveTaskToGroup(data),
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("Moved successfully"),
        variant: "success",
      })
    },
    onError: (error) => {
      const message = error.message
      toast({
        title: translate("Error"),
        description: message || translate("Error occurred while moving task"),
        variant: "error",
      })
    },
  })

  const restoreGroup = (data: any): Promise<any> => {
    return GROUPED_TASKS_SERVICE.restoreGroup(data)
  }

  const restoreTaskGroupMutation = useMutation({
    mutationFn: (data: any) => restoreGroup(data),
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("task-group restored successfully"),
        variant: "success",
      })
    },
    onError: (error) => {
      const message = error.message
      toast({
        title: translate("Error"),
        description: message || translate("Error occurred while restoring task-group"),
        variant: "error",
      })
    },
  })

  const fetchSubTask = (data: IGroupSubTaskInput): Promise<any> => {
    return GROUPED_TASKS_SERVICE.fetchSubTasks(data)
  }

  const fetchSubTaskMutation = useMutation({
    mutationKey: ["fetch-sub"],
    mutationFn: (data: IGroupSubTaskInput) => fetchSubTask(data),
  })

  React.useEffect(() => {
    if (!fetchSubTaskMutation.isPending && fetchSubTaskMutation.isSuccess && projectId) {
      const _grouppedSubTasks = fetchSubTaskMutation?.data?.data ?? []
      if (_grouppedSubTasks && _grouppedSubTasks?.length > 0) {
        const parentId = _grouppedSubTasks[0]?.parent_tasks_id

        // Check if subtasks are already loaded for this parentId
        const group = groupedTasks.find((g) => g._tasks.some((t: any) => t.id === parentId))
        const task = group?._tasks.find((t: any) => t.id === parentId)
        if (task?._subtasks?.length > 0 && task.isLoadingSubTasks === false) {
          if (expandingRow) {
            expandingRow.toggleExpanded()
            setExpandingRow(null)
          }
          return
        }

        // Get stored tasks from localStorage
        const storedData = localStorage.getItem("projects")
        const storedProjects = storedData ? JSON.parse(storedData) : { projects: {} }
        const storedTasks = storedProjects.projects?.[`project_${projectId}`] || {
          project_groups: [],
        }
        const storedGroups = storedTasks.project_groups || []

        // Find the stored subtasks for the parent task
        let storedSubtasks: any[] = []
        for (const group of storedGroups) {
          const storedTask = group._tasks?.find((t: any) => t.id === parentId)
          if (storedTask && storedTask._subtasks) {
            storedSubtasks = storedTask._subtasks
            break
          }
        }

        // Reorder API subtasks based on stored order
        let updatedSubtasks = _grouppedSubTasks
        if (storedSubtasks.length > 0) {
          const storedSubtaskIds = storedSubtasks.map((st: any) => st.id)
          const subtaskMap = new Map(_grouppedSubTasks.map((st: any) => [st.id, st]))
          const reorderedSubtasks = storedSubtaskIds
            .map((id: number) => subtaskMap.get(id))
            .filter((st: any) => st !== undefined)
          const newSubtasks = _grouppedSubTasks.filter(
            (st: any) => !storedSubtaskIds.includes(st.id),
          )
          updatedSubtasks = [...reorderedSubtasks, ...newSubtasks]
        }

        // Update groupedTasks with reordered subtasks
        const nextGrouppedTasks = groupedTasks.map((group) => {
          const { _tasks } = group
          const nextTasks = _tasks.map((task: any) => {
            if (task?.id === parentId) {
              return { ...task, _subtasks: updatedSubtasks, isLoadingSubTasks: false }
            }
            return { ...task }
          })
          return { ...group, _tasks: nextTasks }
        })
        setGroupedTasks(nextGrouppedTasks)

        // Update localStorage with the new subtask list
        const updatedLocalData = {
          ...storedProjects,
          projects: {
            ...storedProjects.projects,
            [`project_${projectId}`]: {
              project_groups: storedGroups.map((g: any) => {
                const updatedTasks = g._tasks?.map((t: any) => {
                  if (t.id === parentId) {
                    return { ...t, _subtasks: updatedSubtasks }
                  }
                  return t
                })
                return { ...g, _tasks: updatedTasks }
              }),
            },
          },
        }
        localStorage.setItem("projects", JSON.stringify(updatedLocalData))
        if (expandingRow) {
          expandingRow.toggleExpanded()
          setExpandingRow(null)
        }
      }
    }
  }, [fetchSubTaskMutation.isPending, fetchSubTaskMutation.isSuccess, projectId, expandingRow])

  const setTaskExpandForLoading = (parentId: number, row: Row<any>) => {
    const nextGrouppedTasks = groupedTasks.map((group) => {
      const { _tasks } = group
      const nextTasks = _tasks.map((task: any) => {
        if (task?.id === parentId) {
          // return { ...task, isLoadingSubTasks: true, row }
          return { ...task, isLoadingSubTasks: true }
        }
        return task
      })
      return { ...group, _tasks: nextTasks }
    })
    setGroupedTasks(nextGrouppedTasks)
    // row.toggleExpanded()
    setExpandingRow(row)
    const payload: IGroupSubTaskInput = {
      parent_task_id: parentId,
      ...(showDeletedTasks && { show_deleted: showDeletedTasks === "true" ? true : false }),
    }
    fetchSubTaskMutation?.mutate(payload)
  }

  const updateGroupTasks = (groupId: number, reorderedTasks: any[]) => {
    setGroupedTasks((prevGroupedTasks) => {
      const updatedGroups = prevGroupedTasks.map((group) => {
        if (group.id === groupId) {
          return { ...group, _tasks: reorderedTasks }
        }
        return group
      })

      // Update localStorage for the specific project
      try {
        const localData = JSON.parse(localStorage.getItem("projects") || "{ projects: {} }")
        if (localData.projects && projectId) {
          const projectKey = `project_${projectId}`
          if (!localData.projects[projectKey]) {
            localData.projects[projectKey] = { project_groups: [] }
          }
          const groupIndex = localData.projects[projectKey].project_groups.findIndex(
            (g: any) => g.id === groupId,
          )
          if (groupIndex !== -1) {
            localData.projects[projectKey].project_groups[groupIndex] = {
              ...localData.projects[projectKey].project_groups[groupIndex],
              _tasks: reorderedTasks,
            }
          } else {
            // Handle case where group is not found (e.g., new group or ungrouped)
            localData.projects[projectKey].project_groups.push({
              id: groupId,
              name: groupId === -1 ? "Ungrouped" : `Group ${groupId}`,
              _tasks: reorderedTasks,
              project_id: parseInt(projectId),
            })
          }
          localStorage.setItem("projects", JSON.stringify(localData))
        }
      } catch (error) {
        console.error("Error updating tasks in local storage:", error)
        toast({
          title: translate("Error"),
          description: translate("Error occurred while updating tasks in local storage"),
          variant: "error",
        })
      }

      return updatedGroups
    })
  }

  // Migrate existing tasks to projects structure
  React.useEffect(() => {
    try {
      const tasksData = localStorage.getItem("tasks")
      if (tasksData && projectId) {
        const parsedTasks = JSON.parse(tasksData)
        const existingProjects = localStorage.getItem("projects")
        let storedProjects = existingProjects ? JSON.parse(existingProjects) : { projects: {} }

        if (!storedProjects.projects) {
          storedProjects = { projects: {} }
        }

        // Move tasks to projects.project_${projectId}
        if (parsedTasks.project_groups) {
          storedProjects.projects[`project_${projectId}`] = parsedTasks
          localStorage.setItem("projects", JSON.stringify(storedProjects))
          localStorage.removeItem("tasks") // Clean up old key
        }
      }
    } catch (error) {
      console.error("Error migrating tasks to projects in local storage:", error)
    }
  }, [projectId])

  const getUsers = async (orgId: string) => {
    const response = await axios.get(
      SERVER_URL + ALL_ORG_USERS_ENDPOINT + `?organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data: orgUsers } = useQuery({
    queryKey: [`org-users`, orgId],
    queryFn: () => getUsers(orgId),
    enabled: !!orgId,
  })

  React.useEffect(() => {
    if (orgUsers) {
      signIn({ organizationUsers: orgUsers?.items })
    }
  }, [orgUsers])

  const getUserName = (activeId: string) => {
    const user = orgUsers?.items?.find((user: any) => user.id == Number(activeId))
    return user?.name || ""
  }

  const getTaskStatus = async () => {
    const response = await axios.get(
      SERVER_URL +
        STATUS_DICTIONARY_ENDPOINT +
        `?context_type=organization%20user&context_id=${orgId}&for_task=true`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data: taskStatus } = useQuery({
    queryKey: ["task-status"],
    queryFn: getTaskStatus,
  })

  function getTaskStatusName(statusList: string[]): string[] {
    const idSet = new Set(statusList.map((id: string) => parseInt(id, 10)))
    if (taskStatus == undefined) return []

    return taskStatus
      .filter((status: any) => idSet.has(status.id))
      .map((status: any) => status.name)
  }

  const getPriorities = async () => {
    const response = await axios.get(
      SERVER_URL + `${PRIORITY_DICTIONARY_ENDPOINT}?organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data: taskPrioritiesList } = useQuery({
    queryKey: ["task-priorities", orgId],
    queryFn: getPriorities,
  })

  return (
    <GroupedTasksContext.Provider
      value={{
        groupedTasks,
        groupedTasksLoading: isLoading,
        groupedTasksRefetch: refetch,
        createGroupMutation,
        updateGroupMutation,
        deleteGroupMutation,
        moveTaskToGroupMutation,
        fetchSubTaskMutation,
        paginationData,
        setPagination,
        setPageSorting,
        pageSorting,
        setTaskExpandForLoading,
        setSelectedTasks,
        selectedTasks,
        setShowColumns,
        showColumns,
        setSearch,
        search,
        updateGroupTasks,
        setGroupedTasks,
        lastInsertTaskId,
        setLastInsertTaskId,
        orgUsers,
        getUserName,
        taskStatus,
        getTaskStatusName,
        taskPrioritiesList,
        restoreTaskGroupMutation,
      }}
    >
      {children}
    </GroupedTasksContext.Provider>
  )
}

export default GroupedTasksProvider
